<template>
  <div>
    <edr-affected-devices-table
      :mitre-data="mitreData"
      :edr-affected-devices="edrAffectedDevices"
      :edr-affected-devices-loading="edrAffectedDevicesLoading"
      :edr-affected-devices-total="edrAffectedDevicesTotal"
      :show-expander="showExpander"
      :findings-to-load="findingsToLoad"
      :show-process-graph-column="true"
      :event-trigger="ticket.eventTrigger"
      @load-more="loadMoreItems"
      @process-graph-click="viewProcessGraph"
    />
    <findings-full-details
      v-if="isBlockedConnection"
      :ticket="ticket"
      :show-title="false"
      class="mt-4"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref } from "vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { type EdrDetectionAffectedDevice, useTicketStore } from "@/_store/tickets/ticket.module";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes";
import { TicketType } from "@/constants/tickets";
import EdrAffectedDevicesTable from "@/components/tickets/ticket-preview-details/base-components/EdrAffectedDevicesTable.vue";
import FindingsFullDetails from "@/components/tickets/ticket-preview-details/full-details/FindingsFullDetails.vue";

export default defineComponent({
  components: {
    EdrAffectedDevicesTable,
    FindingsFullDetails,
  },
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const ticketStore = useTicketStore();
    const router = useRouter();
    const { edrAffectedDevices, edrAffectedDevicesLoading, edrAffectedDevicesTotal } =
      storeToRefs(ticketStore);
    const page = ref(0);

    const mitreData = computed(() => {
      return props.ticket.sections?.edrMetaData?.mitre ?? [];
    });

    const showExpander = computed(() => {
      return props.ticket.sections?.edrMetaData?.showExpandedFindings;
    });

    const findingsToLoad = computed(() => {
      return edrAffectedDevicesTotal.value - (edrAffectedDevices.value ?? []).length;
    });

    const isBlockedConnection = computed(
      () => props.ticket.eventType === TicketType.BLOCKED_CONNECTION
    );

    const viewProcessGraph = (item: EdrDetectionAffectedDevice) => {
      const routeData = router.resolve({
        name: RouteName.PROCESS_GRAPH,
        query: {
          enrollmentCode: item.enrollmentCode,
          processHash: item.processHash,
          processCreationTime: item.processCreationTime,
          pid: item.pid,
          eventId: props.ticket.eventId,
        },
      });
      window.open(routeData.href, "_blank");
    };

    const getItems = async (virtualScroll = false) => {
      await ticketStore.getEdrAffectedDevices({
        page: page.value,
        virtualScroll,
      });
    };

    const loadMoreItems = () => {
      page.value += 1;
      getItems(true);
    };

    onMounted(async () => {
      await getItems();
    });

    return {
      page,
      edrAffectedDevices,
      edrAffectedDevicesLoading,
      edrAffectedDevicesTotal,
      mitreData,
      showExpander,
      findingsToLoad,
      isBlockedConnection,
      loadMoreItems,
      viewProcessGraph,
    };
  },
});
</script>
